import { useReducer } from "react";
import { Kiosk } from "./userAccess/kiosk/KioskContext";
import { StagingSettings, useGetStagingSettings } from "./requests/useGetStagingSettings";
import KaAlert from "./common/lib/KaAlert";
import StageOrderFlow from "./pages/StageOrderFlow/StageOrderFlow";
import GrossWeight from "./pages/GrossWeight";
import { Weight } from "./common/units/useWeight";
import Signature from "./pages/Signature/Signature";
import TicketInstructions from "./pages/TicketInstructions";
import CarrierDriver from "./pages/StageOrderFlow/CarrierDriver";
import StagedOrderNumber from "./pages/StagedOrderNumber";
import { OutboundStagedOrder, OutboundStagedPurchaseOrder } from "./requests/useGetOutboundStagedOrder";
import { KioskStart } from "./pages/KioskStart";
import { useKiosk } from "./userAccess/kiosk/useKiosk";
import AnonymousBayDirections from "./pages/BayDirections/AnonymousBayDirections";
import { StagedOrder, StagedPurchaseOrder } from "./pages/StageOrderFlow/StageOrderTypes";
import useInterval from "./common/lib/hooks/useInterval";
import ReceivingGrossWeight from "./pages/Receiving/GrossWeight/ReceivingGrossWeight";
import ReceivingAnonymousBayDirections from "./pages/Receiving/BayDirections/AnonymousBayDirections";
import ReceivingTareWeight from "./pages/Receiving/TareWeight/ReceivingTareWeight";
import ReceivingSignature from "./pages/Receiving/ReceivingSignature";
import ReceivingTicketInstructions from "./pages/Receiving/ReceivingTicketInstructions";
import { FooterButtonProps } from "./common/FooterButton";
import TareWeight from "./pages/TareWeight/TareWeight";

enum Steps {
  Start,
  CarrierDriver,
  StageOrder,
  Tare,
  BayDirections,
  OrderNumber,
  Gross,
  Sign,
  Ticket,
  ReceivingGross,
  ReceivingBayDirections,
  ReceivingOrderNumber,
  ReceivingTare,
  ReceivingSignature,
  ReceivingTicketInstructions,
}

enum ActionType {
  StagingCancelled,
  SettingsUpdated,
  InboundPicked,
  OutboundPicked,
  Authenticated,
  StagedOrderCreated,
  TareWeightTaken,
  BayDirectionsConfirmed,
  OrderNumberEntered,
  GrossAccepted,
  Signed,
  TicketConfirmed,
  StagedPurchaseOrderCreated,
  ReceivingGrossWeightTaken,
  PurchaseOrderNumberEntered,
  ReceivingTareWeightTaken,
  ReceivingSigned,
  ReceivingTicketConfirmed,
  Back,
}

type Context = { settings: StagingSettings, kiosk: Kiosk };

type State =
  | { step: Steps.Start } & Context
  | { step: Steps.StageOrder } & Context
  | { step: Steps.Tare, so: StagedOrder } & Context
  | { step: Steps.BayDirections, so: StagedOrder } & Context
  | { step: Steps.CarrierDriver } & Context
  | { step: Steps.OrderNumber } & Context
  | { step: Steps.Gross, soId: string, forLoadoutGroup: boolean, tare: Weight } & Context
  | { step: Steps.Sign, soId: string, forLoadoutGroup: boolean } & Context
  | { step: Steps.Ticket, soId: string, forLoadoutGroup: boolean, emailTickets: boolean } & Context
  | { step: Steps.ReceivingGross, spo: StagedPurchaseOrder } & Context
  | { step: Steps.ReceivingBayDirections, spo: StagedPurchaseOrder } & Context
  | { step: Steps.ReceivingTare, spoId: string, gross?: Weight, manualDeliveredEntryRequired: boolean } & Context
  | { step: Steps.ReceivingSignature, spoId: string } & Context
  | { step: Steps.ReceivingTicketInstructions, spoId: string } & Context;

type Action =
  | { type: ActionType.StagingCancelled }
  | { type: ActionType.SettingsUpdated, settings: StagingSettings }
  | { type: ActionType.InboundPicked }
  | { type: ActionType.OutboundPicked }
  | { type: ActionType.StagedOrderCreated, so: StagedOrder }
  | { type: ActionType.TareWeightTaken }
  | { type: ActionType.Authenticated }
  | { type: ActionType.OrderNumberEntered, stagedOrder: OutboundStagedOrder }
  | { type: ActionType.GrossAccepted }
  | { type: ActionType.Signed, emailTickets: boolean }
  | { type: ActionType.BayDirectionsConfirmed }
  | { type: ActionType.TicketConfirmed }
  | { type: ActionType.StagedPurchaseOrderCreated, spo: StagedPurchaseOrder }
  | { type: ActionType.ReceivingGrossWeightTaken }
  | { type: ActionType.PurchaseOrderNumberEntered, outboundStagedPurchaseOrder: OutboundStagedPurchaseOrder }
  | { type: ActionType.ReceivingTareWeightTaken }
  | { type: ActionType.ReceivingSigned }
  | { type: ActionType.ReceivingTicketConfirmed }
  | { type: ActionType.Back };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ActionType.SettingsUpdated:
      return state.step === Steps.Start
        ? { ...state, settings: action.settings }
        : { ...state };

    case ActionType.StagingCancelled:
      return { ...state, step: Steps.Start };

    case ActionType.InboundPicked:
      return { ...state, step: Steps.StageOrder };

    case ActionType.OutboundPicked:
      if (state.settings.requireAuthentication) {
        return { ...state, step: Steps.CarrierDriver };
      }
      return { ...state, step: Steps.OrderNumber };

    case ActionType.Authenticated:
      return { ...state, step: Steps.OrderNumber };

    case ActionType.OrderNumberEntered:
      if (!action.stagedOrder.hasBeenGrossed && action.stagedOrder.requiresGross) {
        const so = action.stagedOrder;
        return {
          ...state,
          step: Steps.Gross,
          soId: so.id,
          forLoadoutGroup: so.forLoadoutGroup,
          tare: { value: so.tareValue, unit: so.tareUnit },
        };
      }
      return { ...state, step: Steps.Sign, soId: action.stagedOrder.id, forLoadoutGroup: action.stagedOrder.forLoadoutGroup };

    case ActionType.GrossAccepted:
      if (state.step === Steps.Gross) {
        return { ...state, step: Steps.Sign };
      }
      break;

    case ActionType.Signed:
      if (state.step === Steps.Sign) {
        return { ...state, ...action, step: Steps.Ticket };
      }
      break;

    case ActionType.StagedOrderCreated:
      if (state.step === Steps.StageOrder) {
        if (action.so.requiresTare)
          return { ...state, ...action, step: Steps.Tare };
        else
          return { ...state, ...action, step: Steps.BayDirections };
      }
      return state;

    case ActionType.TareWeightTaken:
      if (state.step === Steps.Tare) {
        return { ...state, ...action, step: Steps.BayDirections };
      }
      return state;

    case ActionType.StagedPurchaseOrderCreated:
      if (state.step === Steps.StageOrder) {
        if (action.spo.requiresGross)
          return { ...state, ...action, step: Steps.ReceivingGross };
        else
          return { ...state, ...action, step: Steps.ReceivingBayDirections };
      }
      return state;

    case ActionType.ReceivingGrossWeightTaken:
      if (state.step === Steps.ReceivingGross) {
        return { ...state, ...action, step: Steps.ReceivingBayDirections };
      }
      return state;

    case ActionType.PurchaseOrderNumberEntered: 
      const spo = action.outboundStagedPurchaseOrder;
      return {
        ...state,
        step: Steps.ReceivingTare,
        spoId: spo.id,
        gross: !!spo.grossValue && !!spo.grossUnit ? { value: spo.grossValue, unit: spo.grossUnit } : undefined,
        manualDeliveredEntryRequired: !spo.requiresTare,
      };

    case ActionType.ReceivingTareWeightTaken:
      if (state.step === Steps.ReceivingTare) {
        return { ...state, step: Steps.ReceivingSignature };
      }
      return state;

    case ActionType.ReceivingSigned:
      if (state.step === Steps.ReceivingSignature) {
        return { ...state, step: Steps.ReceivingTicketInstructions };
      }
      return state;

    case ActionType.Back:
      if (state.step === Steps.OrderNumber && state.settings.requireAuthentication)
        return { ...state, step: Steps.CarrierDriver };
      break;

    case ActionType.BayDirectionsConfirmed:
    case ActionType.TicketConfirmed:
    case ActionType.ReceivingTicketConfirmed:
  }

  return { ...state, step: Steps.Start };
}

const settingsRefreshTimeMs = 1000 * 30;

const AnonymousFlow = ({ siteId, settings }: { siteId: string, settings: StagingSettings }): JSX.Element => {
  const kiosk = useKiosk();
  const [state, dispatch] = useReducer(reducer, { settings, kiosk, step: Steps.Start });
  const useStagingSettings = useGetStagingSettings(siteId, {
    autoQuery: false,
    onSuccess: settings => dispatch({ type: ActionType.SettingsUpdated, settings })
  });

  useInterval(() => useStagingSettings.query(), settingsRefreshTimeMs, false);

  switch (state.step) {
    case Steps.Start:
      return <KioskStart
        role={state.kiosk.role}
        loadoutOpen={state.settings.loadoutOpen}
        receivingOpen={state.settings.receivingOpen}
        outboundOpen={state.settings.outboundOpen}
        shutOffAccess={state.settings.shutOffAccess}
        shutOffAccessWhenClosed={state.settings.shutOffAccessWhenClosed}
        loadoutHours={state.settings.loadoutHours}
        receivingHours={state.settings.receivingHours}
        timeZone={state.settings.timeZone}
        toInbound={() => dispatch({ type: ActionType.InboundPicked })}
        toOutbound={() => dispatch({ type: ActionType.OutboundPicked })} />

    case Steps.StageOrder:
    case Steps.Tare:
    case Steps.BayDirections:
    case Steps.ReceivingGross:
    case Steps.ReceivingBayDirections:
      return <Inbound state={state} dispatch={dispatch} />

    case Steps.CarrierDriver:
    case Steps.OrderNumber:
    case Steps.Gross:
    case Steps.Sign:
    case Steps.Ticket:
    case Steps.ReceivingTare:
    case Steps.ReceivingSignature:
    case Steps.ReceivingTicketInstructions:
      return <Outbound state={state} dispatch={dispatch} />
  }
}

type SubstateProps = {
  state: State,
  dispatch: React.Dispatch<Action>,
}

const Outbound = ({ state, dispatch }: SubstateProps) => {
  const carrierDriverEntered = () =>
    dispatch({ type: ActionType.Authenticated });

  const stagedOrderReceived = (stagedOrder: OutboundStagedOrder) =>
    dispatch({ type: ActionType.OrderNumberEntered, stagedOrder });

  const stagedPurchaseOrderReceived = (outboundStagedPurchaseOrder: OutboundStagedPurchaseOrder) =>
    dispatch({ type: ActionType.PurchaseOrderNumberEntered, outboundStagedPurchaseOrder });

  const grossWeightAccepted = () =>
    dispatch({ type: ActionType.GrossAccepted });

  const signed = (emailTickets: boolean) =>
    dispatch({ type: ActionType.Signed, emailTickets });

  const ticketConfirmed = () => dispatch({ type: ActionType.TicketConfirmed });
  
  const receivingSigned = () =>
    dispatch({ type: ActionType.ReceivingSigned });

  const receivingTicketConfirmed = () => dispatch({ type: ActionType.ReceivingTicketConfirmed });
  
  const backProps: FooterButtonProps = {
    children: 'Back',
    onClick: () => dispatch({ type: ActionType.Back })
  };

  const startOver = () => dispatch({ type: ActionType.StagingCancelled });

  const backToStartProps: FooterButtonProps = {
    children: 'Back',
    onClick: startOver
  };

  switch (state.step) {
    case Steps.CarrierDriver:
      return <CarrierDriver
        siteId={state.kiosk.siteId}
        enableRememberMe={false}
        carrierDriverEntered={carrierDriverEntered}
        backButton={backToStartProps}
        {...state.settings} 
        startOver={startOver}/>

    case Steps.OrderNumber:
      return <StagedOrderNumber
        siteId={state.kiosk.siteId}
        kioskId={state.kiosk.id}
        stagedOrderReceived={stagedOrderReceived}
        stagedPurchaseOrderReceived={stagedPurchaseOrderReceived}
        backButton={state.settings.requireAuthentication ? backProps : backToStartProps}
        startOver={startOver} />

    case Steps.Gross:
      return <GrossWeight
        siteId={state.kiosk.siteId}
        scaleId={state.kiosk.scaleId!}
        stagedOrderId={state.soId}
        tareWeight={state.tare}
        allowReject={false}
        grossAccepted={grossWeightAccepted}
        officePhoneNumber={state.settings.officePhoneNumber} />

    case Steps.Sign:
      return <Signature
        stagedOrderId={state.soId}
        signatureSubmitted={signed} />

    case Steps.Ticket:
      return <TicketInstructions
        stagedOrderId={state.soId}
        forLoadoutGroup={state.forLoadoutGroup}
        additionalInstructions={undefined}
        loadCompleted={ticketConfirmed} />

    case Steps.ReceivingTare:
      return <ReceivingTareWeight
        spoId={state.spoId}
        siteId={state.kiosk.siteId}
        grossWeight={state.gross}
        tareAccepted={() => dispatch({ type: ActionType.ReceivingTareWeightTaken })}
        scaleId={state.kiosk.scaleId!}
        manualDeliveredAmountEntryRequired={state.manualDeliveredEntryRequired}
        officePhoneNumber={state.settings.officePhoneNumber} />

    case Steps.ReceivingSignature:
      return <ReceivingSignature
        stagedPurchaseOrderId={state.spoId}
        signatureSubmitted={receivingSigned} />
    
    case Steps.ReceivingTicketInstructions:
      return <ReceivingTicketInstructions
        stagedPurchaseOrderId={state.spoId}
        additionalInstructions={undefined}
        receivingCompleted={receivingTicketConfirmed}/>
  }

  return <KaAlert displayMessage={{ message: 'Invalid state: ' + JSON.stringify(state, null, 2), success: false }} />
}

const Inbound = ({ state, dispatch }: SubstateProps) => {
  if (!state.settings.requireAuthentication && state.step === Steps.CarrierDriver) {
    dispatch({ type: ActionType.Authenticated });
  }

  const stagingCancelled = () =>
    dispatch({ type: ActionType.StagingCancelled });

  const stagedOrderCreated = (so: StagedOrder) =>
    dispatch({ type: ActionType.StagedOrderCreated, so });

  const stagedPurchaseOrderCreated = (spo: StagedPurchaseOrder) =>
    dispatch({ type: ActionType.StagedPurchaseOrderCreated, spo });

  const tareWeightTaken = () =>
    dispatch({ type: ActionType.TareWeightTaken });

  const bayDirectionsConfirmed = () =>
    dispatch({ type: ActionType.BayDirectionsConfirmed });

  const receivingGrossWeightTaken = () =>
    dispatch({ type: ActionType.ReceivingGrossWeightTaken });

  switch (state.step) {
    case Steps.StageOrder:
      return <StageOrderFlow
        enableRememberMe={false}
        siteId={state.kiosk.siteId}
        kioskId={state.kiosk.id}
        settings={state.settings}
        stagedOrderCreated={stagedOrderCreated}
        stagedPurchaseOrderCreated={stagedPurchaseOrderCreated}
        backToStart={stagingCancelled} />

    case Steps.Tare:
      return <TareWeight
        siteId={state.kiosk.siteId}
        scaleId={state.kiosk.scaleId!}
        soId={state.so.id}
        requestedAmount={state.so.requestedAmount}
        tareAccepted={tareWeightTaken}
        officePhoneNumber={state.settings.officePhoneNumber} />

    case Steps.BayDirections:
      return <AnonymousBayDirections
        stagedOrderId={state.so.id}
        orderNumberOrLoadoutGroupName={state.so.orderNumberOrLoadoutGroupName}
        allOrderNumbers={state.so.allOrderNumbers}
        shouldPrint={state.settings.printBarcodesAtKiosk}
        bayName={state.so.bayName!}
        confirmed={bayDirectionsConfirmed} />

    case Steps.ReceivingGross:
      return <ReceivingGrossWeight
        siteId={state.kiosk.siteId}
        scaleId={state.kiosk.scaleId!}
        spoId={state.spo.id}
        grossAccepted={() => receivingGrossWeightTaken()}
        officePhoneNumber={state.settings.officePhoneNumber} />

    case Steps.ReceivingBayDirections:
      return <ReceivingAnonymousBayDirections
        stagedPurchaseOrderId={state.spo.id}
        purchaseOrderNumber={state.spo.purchaseOrderNumber}
        bayName={state.spo.bayName!}
        shouldPrint={state.settings.printBarcodesAtKiosk}
        confirmed={bayDirectionsConfirmed} />
  }

  return <KaAlert displayMessage={{ message: 'Invalid state: ' + JSON.stringify(state, null, 2), success: false }} />
}

export default AnonymousFlow;
